<script setup lang="ts">
  // Ported from cartadigital-pos app/components/pos/order/PosOrderCategoryBar.vue.
  import { computed, getCurrentInstance, nextTick, ref, watch } from 'vue';
  import { ICategory } from '@/interfaces/ICategory';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiScrollable from '@/components/pos-v2/ui/PosUiScrollable.vue';

  const props = defineProps<{
    categories: ICategory[];
    // Optional so Vue 2 skips runtime type-checks when null is passed
    // (a `null` entry in the compiled type array is not a valid constructor).
    selectedCategoryId?: number | null;
    loading?: boolean;
    isSearching?: boolean;
    searchQuery?: string;
  }>();

  const emit = defineEmits<{
    (e: 'select', id: number | null): void;
    (e: 'search-pill-click'): void;
  }>();

  const rootEl = ref<HTMLElement>();

  // vue-i18n in script (Vue 2.7): translate through the component proxy.
  const proxy = getCurrentInstance()!.proxy;
  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  const searchChipText = computed(() => {
    const q = String(props.searchQuery ?? '').trim();
    return q
      ? i18nT('posV2.searchWithQuery', { query: q })
      : i18nT('posV2.searchLabel');
  });

  function getScrollEl(): HTMLElement | null {
    return rootEl.value?.querySelector<HTMLElement>('.category-scroll') ?? null;
  }

  // Auto-scroll to the active chip when it changes.
  watch(
    () => [
      props.selectedCategoryId,
      props.isSearching,
      props.categories.length,
    ],
    async () => {
      await nextTick();
      const scrollEl = getScrollEl();
      if (!scrollEl) return;

      if (props.isSearching) {
        scrollEl.scrollTo({ left: 0, behavior: 'smooth' });
        return;
      }

      const activeChip = scrollEl.querySelector('[data-active="true"]');
      if (activeChip) {
        activeChip.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      }
    }
  );
</script>

<template>
  <div ref="rootEl" class="pos-v2-category-bar">
    <PosUiScrollable
      orientation="horizontal"
      variant="pseudo"
      theme="elevated"
      :offset-left="10"
      :offset-right="10"
      size="md"
      :arrows="true"
      scroll-class="category-scroll"
    >
      <!-- Loading -->
      <template v-if="loading">
        <div v-for="i in 5" :key="i" class="chip chip-skeleton">
          <div class="skeleton-content" />
        </div>
      </template>

      <!-- Empty -->
      <div v-else-if="categories.length === 0" class="empty-state">
        <PosUiIcon name="inbox" class="empty-icon" />
        <span class="empty-text">{{ $t('posV2.noCategories') }}</span>
      </div>

      <!-- Categories -->
      <template v-else>
        <button
          v-if="isSearching"
          type="button"
          class="chip chip-search active"
          data-active="true"
          :aria-label="searchChipText"
          @click="emit('search-pill-click')"
        >
          <PosUiIcon name="magnify" class="chip-icon" />
          <span class="chip-label chip-search-label">{{ searchChipText }}</span>
          <PosUiIcon name="close" class="chip-icon chip-icon-close" />
        </button>
        <button
          v-for="category in categories"
          :key="category.id"
          type="button"
          class="chip"
          :class="{
            active: !isSearching && selectedCategoryId === category.id,
            'chip--disabled': !category.visible,
          }"
          :data-active="!isSearching && selectedCategoryId === category.id"
          @click="emit('select', category.id)"
        >
          <PosUiIcon
            v-if="!category.visible"
            name="eye-off"
            class="chip-icon"
          />
          <span class="chip-label">{{ category.name }}</span>
        </button>
      </template>
    </PosUiScrollable>
  </div>
</template>

<style scoped lang="scss">
  // White strip, quiet neutral chips; the active chip is dark (neutral-900) so
  // orange stays reserved for primary actions and prices don't compete.
  .pos-v2-category-bar {
    @apply tw-relative tw-border-b tw-border-neutral-200 tw-bg-white;

    :deep(.category-scroll) {
      @apply tw-flex tw-gap-1.5 tw-overflow-x-auto tw-px-2 tw-py-2;
      scrollbar-width: none;
      -ms-overflow-style: none;

      &::-webkit-scrollbar {
        @apply tw-hidden;
      }
    }

    .chip {
      @apply tw-flex tw-min-h-[40px] tw-flex-shrink-0 tw-items-center tw-gap-1.5 tw-rounded-full tw-border tw-border-transparent tw-bg-neutral-100 tw-px-3.5 tw-py-1.5 tw-text-neutral-700 tw-transition-colors tw-duration-150 hover:tw-bg-neutral-200;

      &.active {
        @apply tw-bg-neutral-900 tw-text-white hover:tw-bg-neutral-800;
      }

      // Disabled categories stay selectable (their items render greyed too).
      &.chip--disabled:not(.active) {
        @apply tw-text-neutral-400;
      }

      .chip-icon {
        @apply tw-text-base;
      }

      .chip-icon-close {
        @apply tw-opacity-90;
      }

      .chip-search-label {
        @apply tw-max-w-[220px] tw-truncate;
      }

      .chip-label {
        @apply tw-whitespace-nowrap tw-text-xs tw-font-medium;
      }
    }

    .chip-skeleton {
      @apply tw-cursor-default;

      .skeleton-content {
        @apply tw-h-4 tw-w-20 tw-animate-pulse tw-rounded tw-bg-neutral-200;
      }
    }

    .empty-state {
      @apply tw-flex tw-items-center tw-gap-1.5 tw-py-1.5 tw-text-neutral-500;

      .empty-icon {
        @apply tw-text-lg;
      }

      .empty-text {
        @apply tw-text-xs;
      }
    }
  }
</style>
